import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, HardDrive, TrendingUp, Upload, ArrowUpRight, Sparkles, Share2, Bell, FileDown, Calendar, AlertCircle } from "lucide-react";
import { api, reportPdfUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import PushNotifications from "@/components/app/PushNotifications";

function fmtSize(bytes) {
  if (!bytes) return "0 B";
  const k = 1024, sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [notif, setNotif] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    Promise.all([api.get("/stats"), api.get("/notifications")])
      .then(([s, n]) => { setStats(s.data); setNotif(n.data); })
      .finally(() => setLoading(false));
  }, []);

  const totalAmount = stats?.by_amount?.reduce((s, x) => s + (x.total || 0), 0) || 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8">
      <div className="flex items-start justify-between mb-10">
        <div>
          <div className="overline text-[#0A0A0A] mb-3">— PANORAMICA</div>
          <h1 className="font-display font-bold text-4xl tracking-tighter">Il tuo archivio</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to="/app/chiedi">
            <Button data-testid="dashboard-assistant-btn" variant="outline" className="rounded-xl border-black/15 hover:bg-black/5 h-10">
              <Sparkles className="w-4 h-4 mr-2" /> Chiedi all'AI
            </Button>
          </Link>
          <a
            href={reportPdfUrl(currentYear)}
            target="_blank"
            rel="noreferrer"
            data-testid="dashboard-report-pdf-btn"
            className="flex items-center gap-2 border border-black/15 hover:bg-black/5 rounded-xl h-10 px-4 text-sm font-medium transition-colors"
          >
            <FileDown className="w-4 h-4" /> Riepilogo {currentYear}
          </a>
          <Link to="/app/upload">
            <Button data-testid="dashboard-upload-btn" className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-10">
              <Upload className="w-4 h-4 mr-2" /> Carica documento
            </Button>
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#8A8A8A]">Caricamento…</div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/10 border border-black/10 rounded-xl mb-6">
            <StatCard testid="stat-total-docs" icon={FileText} label="DOCUMENTI TOTALI" value={stats.total_documents} />
            <StatCard testid="stat-total-size" icon={HardDrive} label="SPAZIO USATO" value={fmtSize(stats.total_size)} />
            <StatCard testid="stat-total-amount" icon={TrendingUp} label="TOTALE ESTRATTO" value={`€ ${totalAmount.toFixed(2)}`} />
          </div>

          {/* Fiscal highlight */}
          {(stats.tax?.isee_count > 0 || stats.tax?.cu730_count > 0) && (
            <div className="border border-[#009966]/25 bg-[#009966]/5 rounded-xl p-6 mb-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-[#009966] flex items-center justify-center rounded-xl">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="overline text-[#009966] mb-1">PRONTO PER IL COMMERCIALISTA</div>
                  <div className="font-display font-bold text-lg tracking-tight">
                    {stats.tax.isee_count} doc. ISEE · {stats.tax.cu730_count} doc. 730/CU
                  </div>
                  <div className="text-xs text-[#5C5C5C] mt-1">
                    Documenti rilevanti già ordinati nelle cartelle dedicate.
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Link to="/app/documenti?tax=isee">
                  <Button data-testid="dashboard-isee-btn" variant="outline" className="rounded-xl border-[#009966]/40 hover:bg-[#009966]/10 h-10">
                    <Share2 className="w-4 h-4 mr-2" /> Cartella ISEE
                  </Button>
                </Link>
                <Link to="/app/documenti?tax=730_cu">
                  <Button data-testid="dashboard-730-btn" className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-10">
                    <Share2 className="w-4 h-4 mr-2" /> Cartella 730/CU
                  </Button>
                </Link>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Push notifications setup */}
            <div className="lg:col-span-3">
              <PushNotifications variant="card" />
            </div>

            {/* Notifications: deadlines + missing */}
            {notif && (notif.deadlines?.length > 0 || notif.missing?.length > 0) && (
              <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6 mb-2">
                {/* Deadlines */}
                <div className="bg-white border border-black/10 rounded-xl">
                  <div className="border-b border-black/10 px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Bell className="w-4 h-4 text-[#0A0A0A]" />
                      <h2 className="font-display font-bold text-lg tracking-tight">Prossime scadenze</h2>
                    </div>
                    <span className="text-xs text-[#8A8A8A]">{notif.deadlines?.length || 0}</span>
                  </div>
                  <div className="p-2">
                    {(notif.deadlines || []).length === 0 ? (
                      <div className="px-4 py-6 text-sm text-[#8A8A8A]">Nessuna scadenza nei prossimi 90 giorni.</div>
                    ) : (
                      <div className="divide-y divide-black/5">
                        {notif.deadlines.slice(0, 5).map(d => (
                          <div key={d.key} className="px-4 py-3 flex items-start gap-3" data-testid={`deadline-${d.key}`}>
                            <div className={`w-1 self-stretch rounded-full ${
                              d.severity === "urgent" ? "bg-[#FF3333]" :
                              d.severity === "past" ? "bg-[#8A8A8A]" : "bg-[#009966]"
                            }`} />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium">{d.title}</div>
                              <div className="text-xs text-[#5C5C5C] leading-relaxed mt-0.5">{d.desc}</div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-xs font-mono-plex text-[#0A0A0A]">{new Date(d.date).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}</div>
                              <div className={`text-[10px] uppercase tracking-widest mt-0.5 ${
                                d.severity === "urgent" ? "text-[#FF3333]" :
                                d.severity === "past" ? "text-[#8A8A8A]" : "text-[#009966]"
                              }`}>
                                {d.days_left >= 0 ? `tra ${d.days_left}g` : `${-d.days_left}g fa`}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Missing */}
                <div className="bg-white border border-black/10 rounded-xl">
                  <div className="border-b border-black/10 px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-[#FFB800]" />
                      <h2 className="font-display font-bold text-lg tracking-tight">Documenti mancanti</h2>
                    </div>
                    <span className="text-xs text-[#8A8A8A]">{notif.missing?.length || 0}</span>
                  </div>
                  <div className="p-2">
                    {(notif.missing || []).length === 0 ? (
                      <div className="px-4 py-6 text-sm text-[#009966]">Tutti i documenti chiave presenti ✓</div>
                    ) : (
                      <div className="divide-y divide-black/5">
                        {notif.missing.slice(0, 6).map(m => (
                          <Link
                            to="/app/upload"
                            key={m.key}
                            data-testid={`missing-${m.key}`}
                            className="block px-4 py-3 hover:bg-black/5 transition-colors"
                          >
                            <div className="text-sm font-medium">{m.title}</div>
                            <div className="text-xs text-[#5C5C5C] leading-relaxed mt-0.5">{m.desc}</div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* By category */}
            <div className="lg:col-span-2 bg-white border border-black/10 rounded-xl">
              <div className="border-b border-black/10 px-6 py-4 flex justify-between items-center">
                <h2 className="font-display font-bold text-lg tracking-tight">Per categoria</h2>
                <Link to="/app/cartelle" className="text-xs text-[#0A0A0A] hover:underline">Vedi cartelle →</Link>
              </div>
              <div className="p-6">
                {stats.by_category.length === 0 ? (
                  <EmptyBlock />
                ) : (
                  <div className="space-y-4">
                    {stats.by_category.map(c => {
                      const pct = stats.total_documents ? Math.round((c.count / stats.total_documents) * 100) : 0;
                      return (
                        <div key={c.category} data-testid={`cat-row-${c.category}`}>
                          <div className="flex justify-between text-sm mb-1.5">
                            <span className="font-medium">{c.category}</span>
                            <span className="font-mono-plex text-[#8A8A8A]">{c.count} • {fmtSize(c.size)}</span>
                          </div>
                          <div className="h-1.5 bg-black/5 rounded-xl overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }}
                              className="h-full bg-[#0A0A0A]"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Recent */}
            <div className="bg-white border border-black/10 rounded-xl">
              <div className="border-b border-black/10 px-6 py-4 flex justify-between items-center">
                <h2 className="font-display font-bold text-lg tracking-tight">Recenti</h2>
                <Link to="/app/documenti" className="text-xs text-[#0A0A0A] hover:underline">Tutti →</Link>
              </div>
              <div className="p-2">
                {stats.recent.length === 0 ? (
                  <div className="px-4 py-8 text-sm text-[#8A8A8A]">Nessun documento ancora.</div>
                ) : (
                  <div className="divide-y divide-black/5">
                    {stats.recent.map(d => (
                      <Link
                        key={d.id}
                        to={`/app/documenti/${d.id}`}
                        data-testid={`recent-doc-${d.id}`}
                        className="flex items-center gap-3 px-3 py-3 hover:bg-black/5 transition-colors group"
                      >
                        <div className="w-9 h-9 shrink-0 bg-[#0A0A0A]/10 flex items-center justify-center rounded-xl">
                          <FileText className="w-4 h-4 text-[#0A0A0A]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm truncate font-medium">{d.smart_filename}</div>
                          <div className="text-xs text-[#8A8A8A] flex gap-2 mt-0.5">
                            <span>{d.category}</span>
                            {d.total_amount && <span>• € {d.total_amount.toFixed(2)}</span>}
                          </div>
                        </div>
                        <ArrowUpRight className="w-4 h-4 text-[#8A8A8A] opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white p-6" data-testid={testid}>
      <div className="flex items-start justify-between mb-6">
        <div className="overline text-[#8A8A8A]">{label}</div>
        <Icon className="w-4 h-4 text-[#0A0A0A]" strokeWidth={1.75} />
      </div>
      <div className="font-display font-black text-4xl tracking-tighter">{value}</div>
    </div>
  );
}

function EmptyBlock() {
  return (
    <div className="border border-dashed border-black/10 rounded-xl p-10 text-center">
      <p className="text-sm text-[#8A8A8A] mb-4">Nessun documento ancora archiviato.</p>
      <Link to="/app/upload">
        <Button className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-10">
          <Upload className="w-4 h-4 mr-2" /> Carica il primo
        </Button>
      </Link>
    </div>
  );
}
